import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Plus, Trash2, Save, Calculator, RefreshCw, ImagePlus, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", ingredients: [], prep_method: "", calories: "", protein: "", carbs: "", fat: "", serving_size: "", notes: "", image_path: "" };

// Per-ingredient nutrient contribution. Foods store nutrients per `serving_size` (default 100, unit 'g').
// We scale by qty / serving_size when the units match (both 'g' or both same unit).
function ingredientContribution(food, qty, unit) {
  if (!food) return { calories: 0, protein: 0, carbs: 0, fat: 0 };
  const q = Number(qty) || 0;
  const base = Number(food.serving_size) || 100;
  // Only scale if unit matches the food's unit (case-insensitive). Otherwise treat as "as-is" serving count.
  const sameUnit = (unit || "g").toLowerCase().trim() === (food.unit || "g").toLowerCase().trim();
  const factor = sameUnit ? (q / base) : q;
  return {
    calories: (Number(food.calories) || 0) * factor,
    protein: (Number(food.protein) || 0) * factor,
    carbs: (Number(food.carbs) || 0) * factor,
    fat: (Number(food.fat) || 0) * factor,
  };
}

const r1 = (n) => Math.round(n * 10) / 10;

export default function RecipeForm() {
  const { id } = useParams();
  const editing = !!id;
  const [form, setForm] = useState(empty);
  const [foods, setFoods] = useState([]);
  const [saving, setSaving] = useState(false);
  // Which nutrient fields were manually edited by the dietitian (won't be overwritten by auto-calc)
  const [overridden, setOverridden] = useState({ calories: false, protein: false, carbs: false, fat: false });
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/foods").then(({ data }) => setFoods(data)).catch(() => {});
    if (editing) {
      api.get("/recipes")
        .then(({ data }) => {
          const r = data.find((x) => x.recipe_id === id);
          if (r) {
            setForm({ ...empty, ...r });
            // When editing existing recipe, assume saved nutrients are intentional — start in overridden mode
            setOverridden({
              calories: r.calories !== null && r.calories !== undefined && r.calories !== "",
              protein: r.protein !== null && r.protein !== undefined && r.protein !== "",
              carbs: r.carbs !== null && r.carbs !== undefined && r.carbs !== "",
              fat: r.fat !== null && r.fat !== undefined && r.fat !== "",
            });
          }
        })
        .catch((e) => toast.error(formatApiError(e)));
    }
  }, [editing, id]);

  // Auto-computed totals from ingredients × food DB
  const totals = useMemo(() => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    for (const ing of form.ingredients) {
      const food = foods.find((f) => f.food_id === ing.food_id);
      const c = ingredientContribution(food, ing.qty, ing.unit);
      t.calories += c.calories; t.protein += c.protein; t.carbs += c.carbs; t.fat += c.fat;
    }
    return { calories: r1(t.calories), protein: r1(t.protein), carbs: r1(t.carbs), fat: r1(t.fat) };
  }, [form.ingredients, foods]);

  // Whenever totals change, auto-fill fields that haven't been manually overridden
  useEffect(() => {
    setForm((p) => ({
      ...p,
      calories: overridden.calories ? p.calories : (form.ingredients.length ? String(totals.calories) : ""),
      protein: overridden.protein ? p.protein : (form.ingredients.length ? String(totals.protein) : ""),
      carbs: overridden.carbs ? p.carbs : (form.ingredients.length ? String(totals.carbs) : ""),
      fat: overridden.fat ? p.fat : (form.ingredients.length ? String(totals.fat) : ""),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totals.calories, totals.protein, totals.carbs, totals.fat, overridden]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const setNutrient = (k, v) => {
    setOverridden((o) => ({ ...o, [k]: true }));
    set(k, v);
  };

  const resetToAuto = () => {
    setOverridden({ calories: false, protein: false, carbs: false, fat: false });
    toast.success("Recalculated from ingredients");
  };

  const fileUrl = (path) => {
    if (!path) return "";
    const token = localStorage.getItem("access_token") || "";
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    return `${base}/api/files/${path}${token ? `?t=${encodeURIComponent(token)}` : ""}`;
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_path", data.storage_path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      // reset input so user can re-pick the same file
      e.target.value = "";
    }
  };

  const removeImage = () => set("image_path", "");

  const addIngredient = () => set("ingredients", [...form.ingredients, { food_id: "", name: "", qty: 100, unit: "g" }]);
  const updIngredient = (i, k, v) => {
    const arr = [...form.ingredients];
    arr[i] = { ...arr[i], [k]: v };
    if (k === "food_id") {
      const f = foods.find((x) => x.food_id === v);
      if (f) { arr[i].name = f.name; arr[i].unit = f.unit; }
    }
    set("ingredients", arr);
  };
  const removeIngredient = (i) => set("ingredients", form.ingredients.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        calories: form.calories === "" ? null : Number(form.calories),
        protein: form.protein === "" ? null : Number(form.protein),
        carbs: form.carbs === "" ? null : Number(form.carbs),
        fat: form.fat === "" ? null : Number(form.fat),
        ingredients: form.ingredients.map((i) => ({ ...i, qty: Number(i.qty) || 0 })),
      };
      if (editing) await api.put(`/recipes/${id}`, payload);
      else await api.post(`/recipes`, payload);
      toast.success(editing ? "Recipe updated" : "Recipe created");
      navigate("/recipes");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const hasIngredients = form.ingredients.length > 0;
  const anyOverridden = overridden.calories || overridden.protein || overridden.carbs || overridden.fat;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="recipe-form-page">
      <Link to="/recipes" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-recipes">
        <ArrowLeft size={16} /> Back to recipes
      </Link>
      <h1 className="text-3xl font-bold">{editing ? "Edit recipe" : "New recipe"}</h1>

      <div className="nt-card p-6 space-y-5">
        <div className="space-y-2">
          <Label>Recipe name</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="recipe-name-input" />
        </div>

        <div className="space-y-2">
          <Label>Photo</Label>
          {form.image_path ? (
            <div className="relative w-full max-w-sm aspect-video rounded-lg overflow-hidden border border-border bg-muted" data-testid="recipe-image-preview">
              <img src={fileUrl(form.image_path)} alt="Recipe" className="absolute inset-0 w-full h-full object-cover" />
              <button
                type="button"
                onClick={removeImage}
                className="absolute top-2 right-2 w-8 h-8 rounded-full bg-background/90 border border-border flex items-center justify-center text-muted-foreground hover:text-destructive"
                aria-label="Remove photo"
                data-testid="recipe-image-remove"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <label
              className={`flex items-center justify-center gap-2 w-full max-w-sm aspect-video rounded-lg border-2 border-dashed border-border bg-muted/30 hover:bg-muted/60 hover:border-primary cursor-pointer transition-colors text-sm text-muted-foreground ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="recipe-image-dropzone"
            >
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
              <span>{uploading ? "Uploading…" : "Add photo (PNG / JPG, ≤ 8 MB)"}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                data-testid="recipe-image-input"
              />
            </label>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <Label>Ingredients</Label>
            <Button type="button" variant="outline" size="sm" onClick={addIngredient} className="gap-1" data-testid="add-ingredient-button"><Plus size={14} /> Add</Button>
          </div>
          <div className="space-y-3">
            {form.ingredients.map((ing, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`ingredient-row-${i}`}>
                <select
                  value={ing.food_id || ""}
                  onChange={(e) => updIngredient(i, "food_id", e.target.value)}
                  className="col-span-6 h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid={`ingredient-select-${i}`}
                >
                  <option value="">— Select food —</option>
                  {foods.map((f) => <option key={f.food_id} value={f.food_id}>{f.name}</option>)}
                </select>
                <Input className="col-span-3" type="number" placeholder="Qty" value={ing.qty} onChange={(e) => updIngredient(i, "qty", e.target.value)} data-testid={`ingredient-qty-${i}`} />
                <Input className="col-span-2" placeholder="Unit" value={ing.unit} onChange={(e) => updIngredient(i, "unit", e.target.value)} data-testid={`ingredient-unit-${i}`} />
                <button onClick={() => removeIngredient(i)} className="col-span-1 text-muted-foreground hover:text-destructive flex justify-center" data-testid={`remove-ingredient-${i}`}><Trash2 size={14} /></button>
              </div>
            ))}
            {form.ingredients.length === 0 && <p className="text-sm text-muted-foreground">No ingredients yet.</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Preparation method</Label>
          <Textarea rows={5} value={form.prep_method} onChange={(e) => set("prep_method", e.target.value)} data-testid="recipe-prep-input" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Calculator size={14} className="text-primary" />
              <Label className="m-0">Nutrition (total for full recipe)</Label>
            </div>
            {hasIngredients && (
              <div className="flex items-center gap-3 text-xs">
                <span className="text-muted-foreground" data-testid="recipe-nutrition-auto-hint">
                  {anyOverridden ? "Some values manually edited" : "Auto-calculated from ingredients"}
                </span>
                {anyOverridden && (
                  <button type="button" onClick={resetToAuto} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid="recipe-recalc-button">
                    <RefreshCw size={12} /> Recalculate
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Calories</Label>
              <Input type="number" value={form.calories} onChange={(e) => setNutrient("calories", e.target.value)} data-testid="recipe-calories-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Protein (g)</Label>
              <Input type="number" value={form.protein} onChange={(e) => setNutrient("protein", e.target.value)} data-testid="recipe-protein-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Carbs (g)</Label>
              <Input type="number" value={form.carbs} onChange={(e) => setNutrient("carbs", e.target.value)} data-testid="recipe-carbs-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Fat (g)</Label>
              <Input type="number" value={form.fat} onChange={(e) => setNutrient("fat", e.target.value)} data-testid="recipe-fat-input" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Serving size</Label>
            <Input value={form.serving_size} onChange={(e) => set("serving_size", e.target.value)} placeholder="e.g. 1 bowl" data-testid="recipe-serving-input" />
          </div>
        </div>

        <div className="space-y-2">
          <Label>Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="recipe-notes-input" />
        </div>

        <Button onClick={save} disabled={saving || !form.name} className="gap-2" data-testid="save-recipe-button">
          <Save size={16} /> {saving ? "Saving…" : "Save recipe"}
        </Button>
      </div>
    </div>
  );
}
